import { useMemo, useState } from 'react';
import styled from 'styled-components';
import TreeView from '@material-ui/lab/TreeView';
import TreeItem from '@material-ui/lab/TreeItem';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import ChevronRightIcon from '@material-ui/icons/ChevronRight';
import Box from '@material-ui/core/Box';
import TextField from '@material-ui/core/TextField';
import CircularProgress from '@material-ui/core/CircularProgress';
import FormControl from '@material-ui/core/FormControl';
import InputLabel from '@material-ui/core/InputLabel';
import Select from '@material-ui/core/Select';
import MenuItem from '@material-ui/core/MenuItem';

import { IFoodlaCategoryOption } from 'models/category';
import useViewport from 'hooks/useViewports';
import { useThrottle } from 'hooks/useThrottle';
import { CategoryTreeType } from './types';
import { getCategoryPathLabel } from './helpers';

const StyledTreeItem = styled(TreeItem)<{ isSelected: boolean }>`
  & > .MuiTreeItem-content > .MuiTreeItem-label {
    background-color: ${props => (props.isSelected ? 'rgba(0, 0, 0, 0.08)' : 'transparent')} !important;
  },
`;

export const findCategory = (categoryList: IFoodlaCategoryOption[] = [], id: string) => {
  if (!categoryList?.length) return;

  let foundCategory: IFoodlaCategoryOption | undefined;

  categoryList.forEach(category => {
    if (foundCategory) return;
    if (category.id === id) foundCategory = category;
    else foundCategory = findCategory(category.children, id);
  });

  return foundCategory;
};

interface ICategoryTreeChildProps {
  readOnly?: boolean;
  type: CategoryTreeType;
  level: number;
  selectedId?: string;
  category: IFoodlaCategoryOption;
  handleToogleExpand: (id: string) => void;
  handleSelect: (id: string, options?: { level?: number; isTerminal?: boolean }) => void;
}

const CategoryTreeChild = ({
  readOnly,
  type,
  level,
  selectedId,
  category,
  handleToogleExpand,
  handleSelect,
}: ICategoryTreeChildProps) => {
  return (
    <StyledTreeItem
      key={category.id}
      isSelected={selectedId === category.id}
      id={category.id}
      nodeId={category.id}
      label={category.name}
      onClick={event => {
        if (readOnly) return;
        // terminal means having no children
        const isTerminal = !category.children?.length;
        if (isTerminal) {
          handleSelect(category.id, { level, isTerminal });
        } else {
          event.stopPropagation();
          handleToogleExpand(category.id);
          // This is required to allow 2nd level selection in foodla category tree
          handleSelect(category.id, { level, isTerminal });
        }
      }}
      onIconClick={event => {
        if (readOnly) return;
        event.stopPropagation();
        handleToogleExpand(category.id);
      }}
    >
      {category.children?.map(category => (
        <CategoryTreeChild
          readOnly={readOnly}
          key={category.id}
          level={level + 1}
          type={type}
          selectedId={selectedId}
          category={category}
          handleToogleExpand={handleToogleExpand}
          handleSelect={handleSelect}
        />
      ))}
    </StyledTreeItem>
  );
};

interface ICategoryTreeProps {
  readOnly?: boolean;
  type: CategoryTreeType;
  loading: boolean;
  hideLabel?: boolean;
  showFullCategory?: boolean;
  error?: string;
  rootCategory?: IFoodlaCategoryOption;
  categories?: IFoodlaCategoryOption[];
  selectedCategory?: IFoodlaCategoryOption;
  onSelect?: (category: IFoodlaCategoryOption) => void;
}

const n = function getName<T extends { name: string }>(input?: T) {
  return input?.name ?? '';
};

interface ISearchOption {
  category: IFoodlaCategoryOption;
  ids: string[];
  parentNames: string[];
}

interface ISearchPreferences {
  searchOnlyTerminalElements?: boolean;
  searchLevelsDeeperThan?: number;
}

function processTreeForSearch(tree: IFoodlaCategoryOption, preferences: ISearchPreferences = {}): ISearchOption[] {
  const result: ISearchOption[] = [];

  const {
    searchOnlyTerminalElements = false,
    searchLevelsDeeperThan = 0, // root is level 0
  } = preferences;

  function traverse(node: IFoodlaCategoryOption, pathIds: string[], pathNames: string[], level: number) {
    const newPathIds = [...pathIds, node.id];
    const newParentNames = [...pathNames]; // exclude node.name

    const isTerminal = !node.children || node.children.length === 0;
    const levelIsDeepEnough = level > searchLevelsDeeperThan;
    const shouldInclude = levelIsDeepEnough && (!searchOnlyTerminalElements || isTerminal);

    if (shouldInclude) {
      result.push({
        category: node,
        ids: newPathIds,
        parentNames: newParentNames,
      });
    }

    node.children?.forEach(child => {
      traverse(child, newPathIds, [...pathNames, node.name], level + 1);
    });
  }

  // Start traversal from level 1 (children of root)
  tree.children?.forEach(child => {
    traverse(child, [], [], 1);
  });

  return result;
}

export const CategoryTree = ({
  readOnly,
  type,
  loading,
  hideLabel,
  showFullCategory,
  error,
  selectedCategory,
  rootCategory,
  categories,
  onSelect,
}: ICategoryTreeProps) => {
  const { mdUp } = useViewport();

  const [searchQuery, setSearchQuery] = useState('');
  const throttledSearchQuery = useThrottle(searchQuery, 500, { leading: false });
  const [expandedIdList, setExpandedIdList] = useState<string[]>([]);
  const [selectedId, setSelectedId] = useState<string>();

  const allSearchOptionList = useMemo(() => {
    if (!rootCategory) return [];

    if (type === 'foodla') {
      const searchOptions = processTreeForSearch(rootCategory, { searchLevelsDeeperThan: 1 });
      return searchOptions;
    }
    if (type === 'ica') {
      const searchOptions = processTreeForSearch(rootCategory, { searchOnlyTerminalElements: true });
      return searchOptions;
    }
    if (type === 'ica_online') {
      const searchOptions = processTreeForSearch(rootCategory, { searchLevelsDeeperThan: 1 });
      return searchOptions;
    }
    if (type === 'coop') {
      const searchOptions = processTreeForSearch(rootCategory);
      return searchOptions;
    }
    if (type === 'wolt_online') {
      const searchOptions = processTreeForSearch(rootCategory, { searchOnlyTerminalElements: true });
      return searchOptions;
    }
    if (type === 'wolt') {
      const searchOptions = processTreeForSearch(rootCategory, { searchOnlyTerminalElements: true });
      return searchOptions;
    }
    if (type === 'apotea') {
      const searchOptions = processTreeForSearch(rootCategory, { searchOnlyTerminalElements: true });
      return searchOptions;
    }
    return [];
  }, [rootCategory, type]);

  const filteredSearchOptionList = useMemo(() => {
    if (loading || error || !throttledSearchQuery) return [];

    return allSearchOptionList
      .map(searchOption => {
        const matchedIndex = (n(searchOption?.category) ?? '')
          .toLowerCase()
          .indexOf(throttledSearchQuery.toLowerCase().trim());
        const matchSubParentIndex = (searchOption?.parentNames?.[1] ?? '')
          .toLowerCase()
          .indexOf(throttledSearchQuery.toLowerCase().trim());
        return { ...searchOption, matchedIndex, matchSubParentIndex };
      })
      .filter(item => item.matchedIndex !== -1 || item.matchSubParentIndex !== -1);
  }, [loading, error, throttledSearchQuery, allSearchOptionList]);

  const handleExpandSelectedCategory = () => {
    if (!selectedCategory?.id) return;
    const searchOption = allSearchOptionList.find(({ ids }) => ids.includes(selectedCategory.id));

    if (!searchOption?.ids?.length) return;
    setExpandedIdList(searchOption.ids.slice(0, searchOption.ids.indexOf(selectedCategory.id)));
  };

  const handleToogleExpand = (id: string) => {
    const newList = expandedIdList.filter(expandedId => expandedId !== id);
    if (!expandedIdList.includes(id)) newList.push(id);
    setExpandedIdList(newList);
  };

  const handleSelect = (id: string, options?: { level?: number; isTerminal?: boolean }) => {
    if (showFullCategory) return;
    if (type === 'foodla') {
      // can select any level deeper than 1
      if (options && options.level === 1) return;
      setSelectedId(id);
      const selectingCategory = findCategory(rootCategory?.children, id);
      if (selectingCategory) onSelect?.(selectingCategory);
      return;
    }

    if (type === 'ica') {
      // can only select deepest level
      if (options && !options.isTerminal) return;
      setSelectedId(id);
      const selectingCategory = categories?.find(item => item.id === id);
      if (selectingCategory) onSelect?.(selectingCategory);
      return;
    }

    if (type === 'ica_online') {
      // can select any level deeper than 1
      if (options && options.level === 1) return;
      setSelectedId(id);
      const selectingCategory = categories?.find(item => item.id === id);
      if (selectingCategory) onSelect?.(selectingCategory);
      return;
    }

    if (type === 'coop') {
      // there's only one level, so can select any level
      setSelectedId(id);
      const selectingCategory = categories?.find(item => item.id === id);
      if (selectingCategory) onSelect?.(selectingCategory);
      return;
    }

    if (type === 'wolt_online') {
      // can only select deepest level
      if (options && !options.isTerminal) return;
      setSelectedId(id);
      const selectingCategory = categories?.find(item => item.id === id);
      if (selectingCategory) onSelect?.(selectingCategory);
      return;
    }

    if (type === 'wolt') {
      // can only select deepest level
      if (options && !options.isTerminal) return;
      setSelectedId(id);
      const selectingCategory = categories?.find(item => item.id === id);
      if (selectingCategory) onSelect?.(selectingCategory);
      return;
    }

    if (type === 'apotea') {
      // can only select deepest level
      if (options && !options.isTerminal) return;
      setSelectedId(id);
      const selectingCategory = categories?.find(item => item.id === id);
      if (selectingCategory) onSelect?.(selectingCategory);
    }
  };

  const selectedPathString = useMemo(() => {
    if (type === 'foodla') return getCategoryPathLabel(selectedCategory, rootCategory);
    if (!selectedCategory?.id) return '';
    const option = allSearchOptionList.find(opt => opt.category.id === selectedCategory.id);
    if (!option) return n(selectedCategory);
    return [...option.parentNames, n(selectedCategory)].join(' -> ');
  }, [selectedCategory, allSearchOptionList, type, rootCategory]);

  return (
    <FormControl fullWidth disabled={readOnly} variant="outlined">
      {!hideLabel && <InputLabel id="category-tree-select">Kategori</InputLabel>}
      <Select
        disabled={readOnly}
        fullWidth
        labelId="category-tree-select"
        label={!hideLabel ? 'Kategori' : undefined}
        value={String(selectedCategory?.id || '')}
        onClose={() => setSearchQuery('')}
        onOpen={handleExpandSelectedCategory}
        MenuProps={{ MenuListProps: { style: { paddingTop: 0 } } }}
        SelectDisplayProps={{ style: { whiteSpace: 'normal', wordBreak: 'break-word' } }}
      >
        {/*
          Because we only use [Select] to render UI only,
          so we need render [MenuItem] to display selected Category
        */}
        <MenuItem value={String(selectedCategory?.id ?? '')} style={{ display: 'none' }}>
          {showFullCategory ? selectedPathString : n(selectedCategory)}
        </MenuItem>

        <Box position="relative" px={2} maxHeight={700} overflow="auto">
          <Box
            position="sticky"
            hidden={readOnly || loading || !!error}
            bgcolor="#FFFFFF"
            zIndex={10}
            top={0}
            py={2}
            onClick={event => event.stopPropagation()}
          >
            <TextField
              fullWidth
              variant="outlined"
              size="small"
              value={searchQuery}
              disabled={readOnly || loading || !!error}
              placeholder="Search category"
              onChange={event => setSearchQuery(event.target.value)}
            />
          </Box>
          {(loading || !!error) && (
            <Box p={2} pt={3} display="flex" justifyContent="center">
              {loading ? <CircularProgress size={24} /> : <Box>Error: Can't load categories</Box>}
            </Box>
          )}

          {!searchQuery && (
            <TreeView
              selected={selectedId}
              expanded={expandedIdList}
              defaultCollapseIcon={<ExpandMoreIcon />}
              defaultExpandIcon={<ChevronRightIcon />}
            >
              {rootCategory?.children?.map(category => (
                <CategoryTreeChild
                  key={category.id}
                  readOnly={readOnly}
                  level={1}
                  type={type}
                  selectedId={selectedCategory?.id}
                  category={category}
                  handleToogleExpand={handleToogleExpand}
                  handleSelect={handleSelect}
                />
              ))}
            </TreeView>
          )}

          {filteredSearchOptionList.map(({ category, parentNames, matchedIndex, matchSubParentIndex }, index) => (
            <MenuItem
              key={index}
              disabled={readOnly}
              value={category.id}
              onClick={() => handleSelect(category.id)}
              style={{
                background: selectedCategory?.id === category.id ? 'rgba(0, 0, 0, 0.08)' : undefined,
                borderTop: !mdUp ? '1px solid #eee' : undefined,
                paddingTop: !mdUp ? 10 : undefined,
                maxWidth: '100%',
              }}
            >
              <div
                style={{
                  display: 'flex',
                  flexWrap: 'wrap',
                  alignItems: mdUp ? 'center' : 'start',
                  flexDirection: mdUp ? 'row' : 'column',
                  width: '100%',
                }}
              >
                {parentNames.map((name, index) => (
                  <span key={index} style={{ display: 'flex' }}>
                    {index > 0 && (
                      <span style={{ display: 'flex', marginLeft: mdUp ? 4 : -4, marginRight: 4, marginTop: 2 }}>
                        <ChevronRightIcon fontSize="small" />
                      </span>
                    )}
                    {index === 0 || matchSubParentIndex === -1 ? (
                      <span style={{ whiteSpace: 'break-spaces' }}>{name}</span>
                    ) : (
                      <span>
                        <span style={{ whiteSpace: 'break-spaces' }}>{name.slice(0, matchSubParentIndex)}</span>
                        <span style={{ background: 'yellow' }}>
                          {name.slice(matchSubParentIndex, matchSubParentIndex + throttledSearchQuery.length)}
                        </span>
                        <span style={{ whiteSpace: 'break-spaces' }}>
                          {name.slice(matchSubParentIndex + throttledSearchQuery.length)}
                        </span>
                      </span>
                    )}
                  </span>
                ))}
                <div style={{ display: 'flex', fontWeight: 'bold', alignItems: 'start' }}>
                  <span style={{ flexShrink: 0, display: 'inline-flex', padding: '0 4px', marginTop: 2 }}>
                    <ChevronRightIcon fontSize="small" />
                  </span>
                  {matchedIndex !== -1 ? (
                    <span>
                      <span style={{ whiteSpace: 'break-spaces' }}>{n(category).slice(0, matchedIndex)}</span>
                      <span style={{ background: 'yellow' }}>
                        {n(category).slice(matchedIndex, matchedIndex + throttledSearchQuery.length)}
                      </span>
                      <span style={{ whiteSpace: 'break-spaces' }}>
                        {n(category).slice(matchedIndex + throttledSearchQuery.length)}
                      </span>
                    </span>
                  ) : (
                    <span style={{ whiteSpace: 'break-spaces' }}>{n(category)}</span>
                  )}
                </div>
              </div>
            </MenuItem>
          ))}
        </Box>
      </Select>
    </FormControl>
  );
};

export default CategoryTree;
